/**
 * The single predicate that decides whether offline order capture is offered.
 *
 * Every refusal is a REASON, never a silent disable — the cashier must be able
 * to tell "the feature is off" from "this terminal isn't prepared", because the
 * fixes are completely different. A greyed-out button with no explanation is
 * what this whole feature exists to replace.
 */

export interface OfflineCapabilityInput {
  /**
   * `navigator.onLine`. Correct when the adapter genuinely drops, but it is
   * NOT sufficient on its own — see `backendReachable`.
   */
  online: boolean
  /**
   * Whether the backend actually answered last time we asked (F-FE-N30).
   *
   * This is the load-bearing signal. In the outage restaurants really have —
   * router up, ISP down — `online` stays true the whole time, so gating on it
   * alone meant offline capture never engaged in the exact scenario it exists
   * for. Defaults to true so a terminal that has not failed anything yet is
   * treated as connected.
   */
  backendReachable?: boolean
  /** Durable offline queue + reserved numbers need the desktop shell (window.bhookly). */
  isDesktop: boolean
  /** How many pre-reserved order numbers this terminal still holds. */
  reservedNumbers: number
}

export type OfflineRefusal = 'online' | 'needs_desktop_app' | 'no_reserved_numbers'

export interface OfflineCapability {
  ok: boolean
  reason?: OfflineRefusal
}

export function offlineCaptureAvailable(i: OfflineCapabilityInput): OfflineCapability {
  // Connected means BOTH: the OS reports a network AND the backend actually
  // answered. Either one saying otherwise puts us in the offline path.
  //
  // Checking only `online` was the original bug: with the router up and the ISP
  // down, `navigator.onLine` never goes false, so the cashier got a
  // "cannot reach the server" error instead of the offline flow.
  const reachable = i.backendReachable !== false
  if (i.online && reachable) return { ok: false, reason: 'online' }

  // Offline sales are held only on the desktop shell: its local storage is
  // scoped to the terminal (and wiped when it is re-pointed at another tenant),
  // whereas a browser tab can lose the queue to a profile clear or a different
  // origin — and a lost queue is a lost sale.
  if (!i.isDesktop) return { ok: false, reason: 'needs_desktop_app' }

  // Without a reserved number the sale would carry a placeholder, and the
  // customer's receipt and the POS would refer to it by different names.
  if (i.reservedNumbers <= 0) return { ok: false, reason: 'no_reserved_numbers' }

  return { ok: true }
}
